import { memo } from 'react';
import { format } from 'date-fns-tz';

import { Countdown } from 'components/countdown';
import PageHeader from 'components/ui/page-header';
import { FOCO_VISIVEL } from 'utils/foco';

import { FaseEcoa } from '../acoes-do-semestre';
import { reguaDaFaseEcoa } from '../etapas-ecoa';

export interface TopoDaFilaEcoaProps {
  /** Fase em exibição (`activeStageObj` da página; na opção semestral, a fase sintética "Semestral"). */
  fase: FaseEcoa | undefined;
  /** Ciclo selecionado. */
  ciclo: { title: string; status: string } | undefined;
  /** Horário do servidor. */
  agora: number;
  /** Ações da fase em exibição com status `aguardando`. */
  pendentes: number;
  /** "Validar Ações ECOA": a página aplica o filtro "Aguardando sua validação". */
  onValidar: () => void;
}

const ddmm = (ms: number) => format(new Date(ms), 'dd/MM');

/** Janela de ciente e validação da fase em exibição (`evaluation_ecoa_days_step2_*`, a mesma da fila). */
function janelaDeValidacao(fase: FaseEcoa | undefined): { inicio: number; fim: number } | undefined {
  const inicio = fase?.evaluation_ecoa_days_step2_start;
  const fim = fase?.evaluation_ecoa_days_step2_finish;
  return typeof inicio === 'number' && typeof fim === 'number' ? { inicio, fim } : undefined;
}

/** ECN-42: selo de estado da fase em exibição. */
export function estadoDaFase(fase: FaseEcoa | undefined, ciclo: TopoDaFilaEcoaProps['ciclo'], agora: number): string {
  if (ciclo && ciclo.status !== 'open') return `Ciclo ${ciclo.title} encerrado`;

  const janela = janelaDeValidacao(fase);
  if (janela && janela.inicio <= agora && agora <= janela.fim) return `Validação aberta até ${ddmm(janela.fim)}`;
  if (janela && agora > janela.fim) return `${fase?.title} · validação encerrada`;
  return `${fase?.title} · validação não iniciada`;
}

function tituloDasPendencias(pendentes: number): string {
  return pendentes === 1 ? '1 Ação ECOA aguardando sua validação' : `${pendentes} Ações ECOA aguardando sua validação`;
}

/** ECN-43: prazo da etapa de ciência e validação, enquanto ela está por vir ou aberta. */
function Prazo({ fase, agora }: { fase: FaseEcoa | undefined; agora: number }) {
  const janela = janelaDeValidacao(fase);
  if (!janela || agora > janela.fim) return null;

  const aberta = agora >= janela.inicio;
  const limite = aberta ? janela.fim : janela.inicio;

  return (
    <>
      {' '}
      <b>Lembre-se:</b> a ciência e validação desta fase {aberta ? 'encerra em' : 'abre em'} <b>{ddmm(limite)}</b> ·{' '}
      <span className="inline-flex rounded-jj-pill bg-brand-yellow px-2.5 py-[3px] font-display text-xs font-bold text-brand-blue-dark">
        <Countdown deadline={new Date(limite)} urgentClassName="text-urgent" />
      </span>
    </>
  );
}

/** Cabeçalho da fila do tutor e do coordenador (ECN-42, ECN-43, ECN-44), no formato do `TopoDoGestor`. */
function TopoDaFilaEcoa({ fase, ciclo, agora, pendentes, onValidar }: TopoDaFilaEcoaProps) {
  // ECN-44: o bloco atual é o que contém o horário do servidor, também na opção semestral.
  const regua = reguaDaFaseEcoa(fase, agora);

  return (
    <>
      <PageHeader
        titulo="Ações ECOA"
        voltar={{ to: '/', destino: 'a página inicial' }}
        selo={
          <span className="rounded-jj-pill bg-brand-purple-light px-3.5 py-[7px] font-display text-[11px] font-bold uppercase tracking-[0.05em] text-brand-navy">
            Perfil coordenador / tutor
          </span>
        }
        acoes={
          // SPEC_DEVIATION: fundo `brand-blue-dark` no lugar do `brand-blue` do design (amarelo de 12px no azul).
          <span className="rounded-jj-pill bg-brand-blue-dark px-4 py-2 font-display text-xs font-bold text-brand-yellow">
            {estadoDaFase(fase, ciclo, agora)}
          </span>
        }
      />

      <section aria-labelledby="pendencias-da-fila-ecoa" className="rounded-jj-panel bg-brand-purple-light px-[30px] pb-[22px] pt-[26px]">
        <p className="m-0 mb-[9px] text-[10.5px] font-bold uppercase tracking-[0.06em] text-brand-navy">
          {`Sua fila de ciência e validação · ${fase?.title ?? ''}`}
        </p>
        <h2
          id="pendencias-da-fila-ecoa"
          className="m-0 mb-2.5 max-w-[620px] font-display text-[25px] font-extrabold leading-[1.2] text-brand-blue-dark"
        >
          {tituloDasPendencias(pendentes)}
        </h2>

        <p className="m-0 mb-4 max-w-[660px] text-[13px] leading-[1.7] text-brand-navy">
          Os integrantes da Ecoa cadastram as ações e você, como coordenador ou tutor, pode dar ciência, validar e bonificar.
          <Prazo fase={fase} agora={agora} />
        </p>

        {/* SPEC_DEVIATION: fundo `brand-blue-dark` no lugar do `brand-blue` do design (amarelo de 12px no azul). */}
        <button
          type="button"
          onClick={onValidar}
          className={`cursor-pointer whitespace-nowrap rounded-jj-sm border-0 bg-brand-blue-dark px-[26px] py-[13px] text-xs font-bold uppercase tracking-[0.04em] text-brand-yellow ${FOCO_VISIVEL}`}
        >
          Validar Ações ECOA
        </button>

        <div className="mt-[18px] min-w-0 rounded-jj-lg bg-brand-white px-4 py-3.5">
          <h3 id="regua-da-fila-ecoa" className="m-0 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-content-soft">
            Fluxo de apreciação da ação ECOA
          </h3>
          <ol aria-labelledby="regua-da-fila-ecoa" className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(132px,1fr))] gap-2 p-0">
            {regua.map(etapa => (
              <li
                key={etapa.titulo}
                aria-current={etapa.atual ? 'step' : undefined}
                className={`flex min-w-0 flex-col gap-[3px] rounded-jj-md px-[11px] py-2 ${
                  etapa.atual ? 'bg-brand-blue-dark text-brand-white' : 'bg-surface-100 text-content'
                }`}
              >
                <b className="font-display text-xs leading-[1.2]">{etapa.titulo}</b>
                <span className="text-[10.5px]">{etapa.periodo ?? 'Sem datas nesta fase'}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
}

/** Memoizado: a página re-renderiza a cada leitura; o topo só muda com a fase, o relógio e as pendências. */
export default memo(TopoDaFilaEcoa);
